/**
 * The organization the dashboard falls back to when none is configured.
 *
 * V1 is fixed to a single organization — Infillion Next — and every hierarchy
 * query is scoped to it. `VITE_MM_DEFAULT_ORG_ID` overrides this, which is how
 * a local checkout points at a different one.
 *
 * QA anonymises organization names, so this id reads as a scrambled name there
 * rather than "Infillion Next". The id is the identity; the name is not.
 */
export const FALLBACK_ORGANIZATION_ID = 102808;

/**
 * Turns a configured value into a usable organization id.
 *
 * Falls back on anything that is not a positive whole number, which covers more
 * than a missing variable. Vite inlines `import.meta.env` at build time, so an
 * image built without it carries `undefined`, and `Number(undefined)` is `NaN`
 * — which reached the API as the literal `organization_id==NaN`. An empty value
 * is the quieter case: `.env.example` ships the key with no value, and
 * `Number("")` is `0`, so a NaN check alone would query `organization_id==0`.
 *
 * Split from the read below because `import.meta.env` is fixed at build time
 * and cannot be varied from a test.
 *
 * @param configured the raw `VITE_MM_DEFAULT_ORG_ID` value, if any
 * @returns the configured id, or {@link FALLBACK_ORGANIZATION_ID}
 */
export const resolveOrganizationId = (
  configured: string | undefined
): number => {
  const parsed = Number(configured);
  return Number.isInteger(parsed) && parsed > 0
    ? parsed
    : FALLBACK_ORGANIZATION_ID;
};

/** The organization id every hierarchy query is scoped to. */
export const getDefaultOrganizationId = (): number =>
  resolveOrganizationId(import.meta.env?.VITE_MM_DEFAULT_ORG_ID);
